// paywalls.ts
// PAYWALL IDS (analytics + attribution)
// - One paywall_id per paywall entry point/placement (where/why the paywall is triggered)
// - Naming: pw_<surface>_<trigger_or_feature>
// - Do NOT rename existing IDs (renames break historical charts)
// - Do NOT include versions in the ID (use paywall_version/variant instead)
// - New paywall entry point => add a new ID here and use it in paywall_shown/paywall_cta_clicked/paywall_dismissed

export type PaywallTrigger =
  | 'feature_gate'
  | 'limit_reached'
  | 'upgrade_cta'
  | 'deep_link'
  | 'other';

const normalizeToken = (value: string) =>
  value.replace(/([a-z0-9])([A-Z])/g, '$1_$2').toLowerCase();

const normalizeHighlight = (highlight?: string | null) => {
  const source = (highlight || 'unknown')
    .replace('theme_remixes', 'theme_creations')
    .replace('grades_whatif_mode', 'grades_whatif');
  return normalizeToken(source);
};

const normalizeRestrictedType = (type: string) => normalizeToken(type);

export const PAYWALL_IDS = {
  TG_UNKNOWN_FEATURE_GATE: 'tg_unknown_feature_gate',
  TG_POST_SIGNUP_TRIAL_OFFER_FEATURE_GATE: 'tg_post_signup_trial_offer_feature_gate',
  TG_STUDY_FILES_LIMIT_REACHED: 'tg_study_files_limit_reached',
  TG_THEME_SWAP_LIMIT_REACHED: 'tg_theme_swap_limit_reached',
  UM_GENERIC_UPGRADE_CTA: 'um_generic_upgrade_cta',
} as const;

export const getTrialGateTrigger = (highlight?: string | null): PaywallTrigger => {
  const normalized = normalizeHighlight(highlight);
  if (normalized === 'theme_swap_limit' || normalized === 'study_files') return 'limit_reached';
  return 'feature_gate';
};

export const getTrialGatePaywallId = (highlight?: string | null): string => {
  const normalized = normalizeHighlight(highlight);
  if (normalized === 'theme_swap_limit') return PAYWALL_IDS.TG_THEME_SWAP_LIMIT_REACHED;
  if (normalized === 'study_files') return PAYWALL_IDS.TG_STUDY_FILES_LIMIT_REACHED;
  return `tg_${normalized}_feature_gate`;
};

export const getUpgradeModalPaywallId = (highlight?: string | null): string => {
  const normalized = normalizeHighlight(highlight || 'generic');
  return `um_${normalized}_upgrade_cta`;
};

const restrictedDialogMap: Record<string, { paywall_id: string; trigger: PaywallTrigger }> = {
  notesLimit: { paywall_id: 'rd_notes_limit_reached', trigger: 'limit_reached' },
  themeSwapLimit: { paywall_id: 'rd_theme_swap_limit_reached', trigger: 'limit_reached' },
  themeSaveLimit: { paywall_id: 'rd_theme_save_limit_reached', trigger: 'limit_reached' },
  transcriptTrialLimit: { paywall_id: 'rd_transcript_trial_limit', trigger: 'limit_reached' },
  transcriptFairUseLimit: { paywall_id: 'rd_transcript_fair_use_limit', trigger: 'limit_reached' },
  filesaiTrialLimit: { paywall_id: 'rd_filesai_trial_limit', trigger: 'limit_reached' },
  filesaiFairUseLimit: { paywall_id: 'rd_filesai_fair_use_limit', trigger: 'limit_reached' },
  accountRequired: { paywall_id: 'rd_account_required_feature_gate', trigger: 'feature_gate' },
  accountRequiredUpgraded: {
    paywall_id: 'rd_account_required_upgraded_feature_gate',
    trigger: 'feature_gate',
  },
  annotationsAccountRequired: {
    paywall_id: 'rd_annotations_account_required',
    trigger: 'feature_gate',
  },
  quoteWidget: { paywall_id: 'rd_quote_widget_feature_gate', trigger: 'feature_gate' },
  musicWidget: { paywall_id: 'rd_music_widget_feature_gate', trigger: 'feature_gate' },
  breatheWidget: { paywall_id: 'rd_breathe_widget_feature_gate', trigger: 'feature_gate' },
  upgradePage: { paywall_id: PAYWALL_IDS.UM_GENERIC_UPGRADE_CTA, trigger: 'upgrade_cta' },
  upgrade: { paywall_id: PAYWALL_IDS.UM_GENERIC_UPGRADE_CTA, trigger: 'upgrade_cta' },
};

export const getRestrictedDialogPaywall = (type: string) => {
  if (restrictedDialogMap[type]) return restrictedDialogMap[type];
  const normalized = normalizeRestrictedType(type);
  return { paywall_id: `rd_${normalized}_feature_gate`, trigger: 'feature_gate' as const };
};
